// Shared live-preview iframe for any proptree-settings page. The agency
// proptree page and the affiliate edit page both render this in their
// right column, and post a `ProptreePreviewPatch` over postMessage to
// the landing app whenever their form state changes — the landing body
// merges the patch over its server-fetched data so unsaved edits are
// visible without a save round-trip.
//
// Extracted from AgencyProptreeSettingsPage so AffiliateSettingsPage can
// reuse it. Callers are responsible for building the patch shape (each
// page maps its own FormState into the patch).
import * as React from 'react';
import { Card } from './ui/card.js';
import { demoQuery, isTestMode } from '../lib/testMode.js';
import { cn } from '../lib/utils.js';

// Where public proptree pages render — both env-var-overridable in dev.
// Kept here (instead of in the calling pages) so the iframe URL is always
// constructed from the same place.
const PUBLIC_URL =
  (import.meta.env.VITE_PUBLIC_URL as string | undefined) ??
  'http://localhost:4040';

// Shape of the postMessage payload the landing page expects. Mirrors
// `ProptreePreviewPatch` in apps/landing/app/[slug]/ProptreeBody.tsx —
// kept in sync by hand. Every field is optional; pages send whichever
// subset their form has touched. Fields a page doesn't surface (e.g.
// agency-only banner controls on the affiliate page) just stay
// undefined — the landing body falls back to the server-fetched value.
export interface ProptreePreviewPatch {
  name?: string | null;
  image?: string | null;
  // Header favicon (enterprise.favicon on the landing side). Sent by the
  // onboarding preview so the scraped favicon shows in the TopBar.
  favicon?: string | null;
  // Avatar fit for `image` — 'contain' keeps wide scraped logos whole
  // instead of cover-cropping them in the circular avatar.
  imageFit?: 'cover' | 'contain';
  buttons?: Array<{
    id: string;
    title: string;
    subtitle: string | null;
    url: string;
    icon: string | null;
    image: string | null;
    images: string[] | null;
    type: string;
  }>;
  agencyControlledButtons?: ProptreePreviewPatch['buttons'];
  agencyBanner?: { visible?: boolean; showStats?: boolean };
  bannerSlogans?: {
    enabled: boolean;
    presets: Record<string, boolean>;
    customs: string[];
  };
  showAgents?: boolean;
  mainFeed?: boolean;
  propertiesType?: string | null;
  property?: string | null;
  showAllAgencyListings?: string | null;
  partners?: Record<string, boolean>;
  newsletter?: boolean;
  // Newsletter card copy; null → the public card's default text.
  newsletterTitle?: string | null;
  newsletterSubtitle?: string | null;
  // Manual listing picker overrides — only consumed by the affiliate
  // edit page; the agency page leaves them undefined.
  chooseListings?: string | null;
  listings?: string[] | null;
}

type Viewport = 'mobile' | 'desktop';

// Width is fixed per viewport so the iframe renders at a real device
// width; height is computed to fill the available preview area at the
// same scale.
const VIEWPORT_WIDTH: Record<Viewport, number> = {
  mobile: 390,
  desktop: 1280,
};

// Live iframe preview of the public proptree page. The src has
// `?preview=1` appended so the landing app activates its postMessage
// listener; this component posts a `ProptreePreviewPatch` to the iframe
// whenever the `patch` prop changes.
export function ProptreePreview({
  slug,
  patch,
  missingSlugMessage = 'Set an alias to preview the public proptree.',
  hideChrome = false,
}: {
  slug: string | null;
  patch: ProptreePreviewPatch;
  missingSlugMessage?: string;
  /** Hide the "Preview" header + viewport toggle and render the frame
   *  edge-to-edge at desktop width. Used by the onboarding wizard. */
  hideChrome?: boolean;
}) {
  const baseUrl = slug ? `${PUBLIC_URL}/${slug}` : null;
  // `slug` may carry its own query string (the onboarding demo passes
  // seed params) — join with '&' in that case. In test mode we also pass
  // `demo=1` (plus `e=<enterpriseId>` when this brokerage has its own
  // imported set) so the landing app renders a demo dataset instead of
  // querying the API for a proptree that was never saved.
  const url = baseUrl
    ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}preview=1${
        isTestMode() ? `&${demoQuery()}` : ''
      }`
    : null;
  const [viewport, setViewport] = React.useState<Viewport>('desktop');

  return (
    <Card className="flex h-full min-h-[640px] flex-col overflow-hidden p-0 lg:min-h-0">
      {!hideChrome && (
        <div className="relative flex shrink-0 items-center justify-center border-b border-[hsl(var(--hairline))] px-5 py-3">
          <span className="mono-label absolute left-5 text-muted-foreground">
            Preview
          </span>
          <ViewportToggle value={viewport} onChange={setViewport} />
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden bg-muted/40">
        {url ? (
          <ScaledFrame
            key={slug}
            url={url}
            viewport={hideChrome ? 'desktop' : viewport}
            patch={patch}
          />
        ) : (
          <div className="flex h-full items-center justify-center px-6 text-center font-mono text-[11px] text-muted-foreground">
            {missingSlugMessage}
          </div>
        )}
      </div>
    </Card>
  );
}

// Drives the cross-origin postMessage bridge to the preview iframe.
// Re-posts the patch whenever it changes, and re-posts on every
// `proptr:preview-ready` handshake so internal navigation inside the
// iframe (or the initial load race) still ends up patched. targetOrigin
// is `*` because the iframe origin differs from the manager's in both
// dev and prod; the payload is non-sensitive form state, not credentials.
function usePreviewBridge(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  patch: ProptreePreviewPatch,
) {
  const patchRef = React.useRef(patch);
  React.useEffect(() => {
    patchRef.current = patch;
  }, [patch]);

  React.useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'proptr:preview', patch },
      '*',
    );
  }, [iframeRef, patch]);

  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const d = e.data;
      if (d && typeof d === 'object' && d.type === 'proptr:preview-ready') {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'proptr:preview', patch: patchRef.current },
          '*',
        );
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [iframeRef]);
}

function ViewportToggle({
  value,
  onChange,
}: {
  value: Viewport;
  onChange: (v: Viewport) => void;
}) {
  return (
    <div className="inline-flex shrink-0 rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
      {(['mobile', 'desktop'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cn(
            'rounded px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.06em] transition-colors',
            value === v
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

// Mobile mode fills the preview area at native size — no scaling. Desktop
// renders at a real desktop width (1280px) and is scaled down via CSS
// transform to fit the available width, with iframe height computed so
// the rendered page fills the full preview height.
function ScaledFrame({
  url,
  viewport,
  patch,
}: {
  url: string;
  viewport: Viewport;
  patch: ProptreePreviewPatch;
}) {
  if (viewport === 'mobile') {
    return <MobileFrame url={url} patch={patch} />;
  }
  return <DesktopScaledFrame url={url} patch={patch} />;
}

function MobileFrame({
  url,
  patch,
}: {
  url: string;
  patch: ProptreePreviewPatch;
}) {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  usePreviewBridge(iframeRef, patch);
  return (
    <iframe
      ref={iframeRef}
      src={url}
      title="Proptree preview"
      className="h-full w-full border-0 bg-background"
    />
  );
}

function DesktopScaledFrame({
  url,
  patch,
}: {
  url: string;
  patch: ProptreePreviewPatch;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [size, setSize] = React.useState({ scale: 1, nativeH: 800 });
  const nativeW = VIEWPORT_WIDTH.desktop;
  usePreviewBridge(iframeRef, patch);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      const scale = Math.min(w / nativeW, 1);
      const nativeH = scale > 0 ? h / scale : h;
      setSize({ scale, nativeH });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [nativeW]);

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full items-start justify-center overflow-hidden"
    >
      <div
        style={{
          width: nativeW * size.scale,
          height: size.nativeH * size.scale,
        }}
      >
        <iframe
          ref={iframeRef}
          src={url}
          title="Proptree preview"
          style={{
            width: nativeW,
            height: size.nativeH,
            transform: `scale(${size.scale})`,
            transformOrigin: 'top left',
          }}
          className="border-0 bg-background"
        />
      </div>
    </div>
  );
}
